import React, { useState } from "react";
import { MdExpandMore, MdExpandLess, MdHelpOutline } from "react-icons/md";
import Avatar from "components/avatar";
import Badge from "components/badge";
import { IconButton } from "components/button";
import { CheckInBadge, fullName, roleLabel } from "./teamDisplay";

export const countDescendants = (node) =>
  (node.children || []).reduce((sum, c) => sum + 1 + countDescendants(c), 0);

const OrgChartNode = ({ node, depth = 0, onSelect, dnd }) => {
  const [expanded, setExpanded] = useState(depth < 3);
  const [dragOver, setDragOver] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const name = fullName(node);

  const dragEnabled = Boolean(dnd?.enabled);
  const isDragging = dnd?.draggedId === node.id;
  const isDropBlocked = dragEnabled && dnd?.draggedId && dnd.invalidIds?.has(node.id);
  const isDropTarget = dragEnabled && dnd?.draggedId && !isDropBlocked;

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <div
          role="button"
          tabIndex={0}
          onClick={() => onSelect && onSelect(node)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            onSelect && onSelect(node);
          }}
          draggable={dragEnabled}
          onDragStart={(e) => {
            if (!dragEnabled) return;
            e.stopPropagation();
            e.dataTransfer.effectAllowed = "move";
            dnd.onDragStart(node);
          }}
          onDragEnd={() => dragEnabled && dnd.onDragEnd()}
          onDragOver={(e) => {
            if (!isDropTarget) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "move";
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            if (!isDropTarget) return;
            e.preventDefault();
            e.stopPropagation();
            setDragOver(false);
            dnd.onDrop(node);
          }}
          className={`focus-ring flex w-52 flex-col items-center rounded-card border bg-surface p-3 text-center shadow-card transition hover:-translate-y-0.5 dark:shadow-none ${
            isDragging
              ? "border-accent opacity-40"
              : dragOver
              ? "border-primary ring-2 ring-accent"
              : isDropBlocked
              ? "border-line opacity-50"
              : "border-line hover:border-accent"
          } ${dragEnabled ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
        >
          <Avatar user={node} name={name} size="md" noBorder />
          <span className="mt-2 block w-full truncate text-sm font-bold text-fg">{name}</span>
          <span className="block w-full truncate text-xs text-fg-muted">
            {node.jobTitle || roleLabel(node.role)}
          </span>
          <span className="mt-2 flex flex-wrap items-center justify-center gap-1">
            <Badge size="sm">{roleLabel(node.role)}</Badge>
            {/* People at the top of a branch have nothing to submit */}
            {node.managerId && <CheckInBadge submitted={node.hasSubmittedThisWeek} />}
            {node.activeQuestionCount > 0 && (
              <Badge
                size="sm"
                title={`${node.activeQuestionCount} check-in question${
                  node.activeQuestionCount === 1 ? "" : "s"
                } for their team`}
              >
                <MdHelpOutline aria-hidden="true" className="h-3 w-3" />
                {node.activeQuestionCount}
                <span className="sr-only">
                  {` check-in question${node.activeQuestionCount === 1 ? "" : "s"}`}
                </span>
              </Badge>
            )}
          </span>
        </div>
        {hasChildren && (
          <IconButton
            icon={expanded ? <MdExpandLess /> : <MdExpandMore />}
            label={expanded ? `Collapse ${name}'s team` : `Expand ${name}'s team`}
            variant="secondary"
            size="sm"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            className="absolute -bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full shadow-card dark:shadow-none"
          />
        )}
      </div>

      {hasChildren && expanded && (
        <>
          <div className="h-8 w-px bg-line" />
          <div className="flex items-start">
            {node.children.map((child, i) => {
              const isFirst = i === 0;
              const isLast = i === node.children.length - 1;
              const only = node.children.length === 1;
              return (
                <div key={child.id} className="relative flex flex-col items-center px-3">
                  {!only && (
                    <div
                      className={`absolute top-0 h-px bg-line ${
                        isFirst ? "left-1/2 right-0" : isLast ? "left-0 right-1/2" : "left-0 right-0"
                      }`}
                    />
                  )}
                  <div className="h-8 w-px bg-line" />
                  <OrgChartNode node={child} depth={depth + 1} onSelect={onSelect} dnd={dnd} />
                </div>
              );
            })}
          </div>
        </>
      )}

      {hasChildren && !expanded && (
        <p className="mt-5 text-xs font-medium text-fg-muted">{countDescendants(node)} below</p>
      )}
    </div>
  );
};

export default OrgChartNode;
